import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import type { IOvieHealthTrendPoint } from '@principle-theorem/principle-core/interfaces';
import { DAY_MONTH_YEAR_FORMAT } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { buildEchartsTheme } from '../echarts-theme';

@Component({
  selector: 'pr-echarts-line-chart',
  templateUrl: './echarts-line-chart.component.html',
  styleUrls: ['./echarts-line-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsLineChartComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IOvieHealthTrendPoint[]>([]);
  readonly label = input.required<string>();
  readonly yAxisLabel = input<string>();
  readonly height = input<number>(280);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const points = this.data();
    if (points.length === 0) {
      return this.label();
    }
    const peak = points.reduce((max, point) =>
      point.count > max.count ? point : max
    );
    return `${this.label()} line chart, ${points.length} data points, peak on ${
      peak.date
    } at ${peak.count}`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const points = this.data();
    if (points.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);
    const dates = points.map((point) =>
      moment(point.date).format(DAY_MONTH_YEAR_FORMAT)
    );
    const counts = points.map((point) => point.count);

    return {
      ...theme,
      xAxis: {
        ...(theme.xAxis as Record<string, unknown>),
        type: 'category',
        data: dates,
        boundaryGap: false,
      },
      yAxis: {
        ...(theme.yAxis as Record<string, unknown>),
        type: 'value',
        name: this.yAxisLabel(),
      },
      series: [
        {
          type: 'line',
          name: this.label(),
          smooth: true,
          data: counts,
          symbol: 'circle',
          symbolSize: 6,
        },
      ],
    };
  });
}
